import { useState } from 'nuxt/app';
import { computed, readonly, unref } from 'vue';
import { randomUUID } from 'uncrypto';

const CHAT_SESSION_KEY = (orgId: string) => `ai_chat_session_${orgId}`;
const CHAT_SESSION_LIMIT_IN_MS = 2 * 60 * 60 * 1000; // 2 hours

export interface StoredChatSession {
  sessionId: string;
  expiresAt: number;
  conversationId?: string;
  userToken?: string;
}

export function getStoredChatSession(orgId: string): StoredChatSession | null {
  if (!import.meta.client || !orgId) return null;
  try {
    // uses the orgId to get the StoredChatSession from localStorage and removes session key once expired
    const raw = localStorage.getItem(CHAT_SESSION_KEY(orgId));
    if (!raw) return null;
    const parsed: StoredChatSession = JSON.parse(raw);
    if (Date.now() > parsed.expiresAt) {
      clearChatSession(orgId);
      return null;
    }
    return parsed;
  } catch (error) {
    console.error('Failed to get stored chat session:', error);
    return null;
  }
}

export function setStoredChatSession(
  orgId: string,
  data: { sessionId: string; conversationId?: string; userToken?: string },
): void {
  if (!import.meta.client) return;
  try {
    const stored: StoredChatSession = {
      sessionId: data.sessionId,
      conversationId: data.conversationId,
      userToken: data.userToken,
      expiresAt: Date.now() + CHAT_SESSION_LIMIT_IN_MS,
    };
    localStorage.setItem(CHAT_SESSION_KEY(orgId), JSON.stringify(stored));
  } catch (error) {
    console.error('Failed to set stored chat session:', error);
  }
}

export function clearChatSession(orgId: string): void {
  if (import.meta.client && orgId) {
    try {
      localStorage.removeItem(CHAT_SESSION_KEY(orgId));
    } catch {
      // Ignore storage errors
    }
  }
}

export function useSession(orgId: string) {
  const resetCounter = useState<Record<string, number>>('chat_session_reset', () => ({}));

  const sessionId = computed(() => {
    const organizationId = unref(orgId) ?? '';
    // Trigger re-computation when resetSession is called for this org
    void resetCounter.value[organizationId];
    if (!import.meta.client || !organizationId) return '';
    // return the stored session id if one exists; otherwise create a new one, store it, and return it.
    const stored = getStoredChatSession(organizationId);
    const id = stored?.sessionId ?? randomUUID();
    if (!stored) setStoredChatSession(organizationId, { sessionId: id });
    return id;
  });

  const resetSession = (resetOrgId: string) => {
    if (import.meta.client && resetOrgId) {
      clearChatSession(resetOrgId);
      resetCounter.value = { ...resetCounter.value, [resetOrgId]: (resetCounter.value[resetOrgId] ?? 0) + 1 };
    }
  };

  return { sessionId: readonly(sessionId), resetSession };
}
